import { useCallback, useEffect, useState } from 'react';
import {
  FEATURE_REQUEST_STATUSES,
  MAX_FEATURE_REQUEST_MESSAGE_LENGTH,
  featureRequestStatusLabel,
  type FeatureRequestStatus,
} from '@srcbattle/shared';
import { api, ApiRequestError, type FeatureRequest } from '../api.ts';
import { Avatar, Banner, Button, Card } from './ui.tsx';
import { FeatureRequestThread, StatusBadge, UnreadDot } from './FeatureRequestThread.tsx';

/** The three decisions, in the order they get made. */
const DECISIONS: Array<{ status: FeatureRequestStatus; label: string; ask: string }> = [
  { status: 'accepted', label: 'Accept', ask: 'Anything to tell them? (optional)' },
  { status: 'completed', label: 'Mark done', ask: 'Anything to tell them? (optional)' },
  { status: 'declined', label: 'Decline', ask: 'Why not? They will see this.' },
];

/**
 * Everything the club has asked for, and what to do about it.
 *
 * Collapsed by default like the other admin sections, with the count of things
 * still waiting on the summary line — the one number worth seeing without
 * opening anything.
 *
 * Accepting, declining and marking done all take an optional note, which lands
 * in the same thread as the status change. A decline asks for the reason rather
 * than offering it: "no" with no explanation is the thing that stops people
 * asking again.
 */
export function FeatureRequestsAdmin() {
  const [requests, setRequests] = useState<FeatureRequest[] | null>(null);
  const [filter, setFilter] = useState<FeatureRequestStatus | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    () =>
      api
        .adminFeatureRequests()
        .then(({ requests: found }) => setRequests(found))
        .catch(() => setError('Could not load the requests.')),
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * A reply or a decision updates that one thread in place.
   *
   * Reloading the list would be simpler and would empty the thread being read:
   * a list carries no messages, so the admin would answer a request and watch
   * the conversation they were answering vanish.
   */
  async function patch(work: () => Promise<{ request: FeatureRequest }>) {
    setBusy(true);
    setError(null);
    try {
      const { request: updated } = await work();
      setRequests(
        (current) => current?.map((item) => (item.id === updated.id ? updated : item)) ?? current,
      );
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  function toggle(request: FeatureRequest) {
    if (openId === request.id) {
      setOpenId(null);
      return;
    }
    setOpenId(request.id);
    void api
      .featureRequest(request.id)
      .then(({ request: full }) => {
        setRequests((current) =>
          current?.map((item) => (item.id === full.id ? { ...full, hasUnread: false } : item)) ?? current,
        );
        return api.markFeatureRequestSeen(request.id);
      })
      .catch(() => setError('Could not open that request.'));
  }

  function decide(request: FeatureRequest, decision: (typeof DECISIONS)[number]) {
    const note = window.prompt(`${decision.label} “${request.title}”.\n\n${decision.ask}`, '');
    // Cancel means cancel; an empty box means "no note", which is allowed.
    if (note === null) return;
    void patch(() =>
      api.setFeatureRequestStatus(
        request.id,
        decision.status,
        note.trim().slice(0, MAX_FEATURE_REQUEST_MESSAGE_LENGTH) || undefined,
      ),
    );
  }

  const waiting = (requests ?? []).filter((request) => request.status === 'open').length;
  const unread = (requests ?? []).filter((request) => request.hasUnread).length;
  const shown = (requests ?? []).filter(
    (request) => filter === 'all' || request.status === filter,
  );

  return (
    <Card className="mb-6 p-0">
      <details>
        <summary className="flex cursor-pointer items-center gap-1 px-4 py-3 font-medium">
          <span>
            Feature requests
            {requests ? ` (${waiting} waiting of ${requests.length})` : ''}
          </span>
          <UnreadDot count={unread} label={`${unread} with something new`} />
        </summary>

        <div className="border-t border-border px-4 py-3">
          {error && (
            <div className="mb-3">
              <Banner tone="danger">{error}</Banner>
            </div>
          )}

          <div className="mb-3 flex flex-wrap gap-2">
            {(['all', ...FEATURE_REQUEST_STATUSES] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setFilter(value)}
                aria-pressed={filter === value}
                className={`rounded-lg border px-2.5 py-1.5 text-xs ${
                  filter === value ? 'border-accent bg-accent/15' : 'border-border text-text-muted'
                }`}
              >
                {value === 'all' ? 'All' : featureRequestStatusLabel(value)}
              </button>
            ))}
          </div>

          {requests === null ? (
            <p className="text-sm text-text-faint">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="text-sm text-text-faint">
              {requests.length === 0 ? 'Nobody has asked for anything yet.' : 'None in that state.'}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {shown.map((request) => (
                <li key={request.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Avatar value={request.authorAvatar} size="sm" />
                    <button
                      type="button"
                      onClick={() => toggle(request)}
                      aria-expanded={openId === request.id}
                      className="min-w-0 flex-1 text-left"
                    >
                      <span className="flex items-center gap-1 text-sm font-medium">
                        <span className="truncate">{request.title}</span>
                        <UnreadDot
                          count={request.hasUnread ? 1 : 0}
                          label="Something new from the member"
                        />
                      </span>
                      <span className="block text-xs text-text-faint">
                        {request.authorName} ·{' '}
                        {new Date(request.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                        {request.messageCount > 0 && ` · ${request.messageCount} in the thread`}
                      </span>
                    </button>
                    <StatusBadge status={request.status} />
                  </div>

                  {openId === request.id && (
                    <div className="mt-3">
                      <FeatureRequestThread
                        request={request}
                        busy={busy}
                        onReply={(text) =>
                          void patch(() => api.replyToFeatureRequest(request.id, text))
                        }
                      />
                      <div className="mt-3 flex flex-wrap gap-2">
                        {DECISIONS.filter((decision) => decision.status !== request.status).map(
                          (decision) => (
                            <Button
                              key={decision.status}
                              variant={decision.status === 'declined' ? 'danger' : 'secondary'}
                              disabled={busy}
                              onClick={() => decide(request, decision)}
                            >
                              {decision.label}
                            </Button>
                          ),
                        )}
                        {request.status !== 'open' && (
                          <Button
                            variant="ghost"
                            disabled={busy}
                            onClick={() =>
                              void patch(() => api.setFeatureRequestStatus(request.id, 'open'))
                            }
                          >
                            Reopen
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          disabled={busy}
                          onClick={() => {
                            if (
                              !window.confirm(
                                `Delete “${request.title}”?\n\nThis removes the request and its whole thread for both of you.`,
                              )
                            )
                              return;
                            setOpenId(null);
                            void run(() => api.deleteFeatureRequest(request.id));
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
    </Card>
  );
}
